import { Injectable, OnDestroy } from "@angular/core";
import { TranslateService } from "@ngx-translate/core";
// multi-asset
import * as CubiqTS from "shared/cubiq-model-multi-asset";
///

import { LoggerService } from "@bfx/logger";
const logger = LoggerService.for(__dirname);

import { OESettingsService } from "./oe-settings.service";
import {
  ICAutocompleteGenericModel,
  ICFixingSourceModel,
} from "../../interface/controls/models/bfx-interface-controls.models";
import {
  AlgoStrategyPromiseLookupService,
  BenchmarkDataPrimaryLookupService,
  CcyPairPromiseLookupService,
  CustomFieldPromiseLookupService,
  FixingSourcePromiseLookupService,
} from "shared/cubiq-services";
import { Subscription, take } from "rxjs";
import { MappedCurrencyPairCalendarImpl } from "shared/cubiq-services/model/mapped/MappedCurrencyPairCalendarImpl";
import { BenchmarkFixingCalendarLookupService } from "shared/cubiq-services/lookup/model/impl/FixingCalendarLookupService";
import {
  BenchmarkFixingPrimaryLookupService,
  BenchmarkFixingTime,
} from "shared/cubiq-services/lookup/model/impl/BenchmarkFixingPrimaryLookupService";

@Injectable()
export class OEDatasetService implements OnDestroy {
  /**********************************************************************/
  /* DATA */
  public control: any = {
    orderTypeDatasetInitialized: false,
  };

  public orderType: any = {
    dataset: null,
    datasetWithoutTSL: null,
    datasetWithoutMKT: null,
    datasetWithoutTSL_MKT: null,
    datasetGamma: null,
  };

  public ccyPairs: {
    datasetAll: Array<string>;
    datasetAlgo: Array<string>;
    datasetBmark: Array<string>;
    datasetNdf: Array<string>;
    datasetSpotFwd: Array<string>;
    datasetSpotFwdMKT: Array<string>;
    datasetGamma: Array<string>;
    datasetNDFAlgo: Array<string>;
  } = {
    datasetAll: [],
    datasetAlgo: [],
    datasetBmark: [],
    datasetNdf: [],
    datasetSpotFwd: [],
    datasetSpotFwdMKT: [],
    datasetGamma: [],
    datasetNDFAlgo: [],
  };

  public fixingSources: any = {
    dataset: null,
  };

  public bmFixingSources: any = {
    dataset: null,
    allBmFixings: null,
  };

  /** List of visible custom fields for editing/modification sorted by ID */
  public customFieldDefns: {
    editable: Array<CubiqTS.CustomField>;
    noneditable: Array<CubiqTS.CustomField>;
  } = {
    editable: [],
    noneditable: [],
  };

  private interval = null;
  private pairUpdateSub: any;
  private fixSrcSub: Subscription = null;
  private bmFixSrcSub: Subscription = null;
  /**********************************************************************/
  /* CONSTRUCTOR */
  constructor(
    private translate: TranslateService,
    private settings: OESettingsService,
    private algoLookup: AlgoStrategyPromiseLookupService,
    private ccyPairLookup: CcyPairPromiseLookupService,
    private fixingSourceLookup: FixingSourcePromiseLookupService,
    private customFieldService: CustomFieldPromiseLookupService,
    protected benchmarkCalendarService: BenchmarkFixingCalendarLookupService,
    protected benchmarkFixingService: BenchmarkFixingPrimaryLookupService,
  ) {
    this.settings.primed$.pipe(take(1)).subscribe(() => {
      this.initialize();
    });
  }

  /**********************************************************************/
  /* INITIALIZE */

  private initialize(): void {
    this.initializeOrderTypeDataset();
    this.initializeCcyPairsDataset();
    //this.initializeAlgoTypeDataset();
    this.initializeFixingCodeDataset();
    this.initializeCustomFieldDefns();
    this.initializeBmFixingCodeDataset();

    this.registerForDataUpdates();
  }

  registerForDataUpdates() {
    this.pairUpdateSub = this.ccyPairLookup
      .getModelUpdateSubject()
      .subscribe(this.onPairsUpdated.bind(this));
    this.fixSrcSub = this.fixingSourceLookup
      .getModelUpdateSubject()
      .subscribe(this.onFixingSourceUpdated.bind(this));
    this.bmFixSrcSub = this.benchmarkFixingService
      .getModelUpdateSubject()
      .subscribe(this.onBmFixingSourceUpdated.bind(this));
  }

  ngOnDestroy() {
    if (this.pairUpdateSub) this.pairUpdateSub.unsubscribe();
    if (null != this.fixSrcSub) this.fixSrcSub.unsubscribe();

    if (this.bmFixSrcSub != null) {
      this.bmFixSrcSub.unsubscribe();
    }
  }

  protected onPairsUpdated(modelEvent: Array<any>): void {
    this.initializeCcyPairsDataset();
  }

  private initializeOrderTypeDataset(): void {
    this.orderType.dataset = new Array<ICAutocompleteGenericModel>();
    this.orderType.datasetWithoutTSL = new Array<ICAutocompleteGenericModel>();
    this.orderType.datasetWithoutMKT = new Array<ICAutocompleteGenericModel>();
    this.orderType.datasetWithoutTSL_MKT = new Array<ICAutocompleteGenericModel>();
    this.orderType.datasetGamma = new Array<ICAutocompleteGenericModel>();

    this.settings.getSysConfigForOrderTypes().then((config) => {
      this.translate.get("isTranslatePrimed").subscribe((test: string) => {
        // load translation
        CubiqTS.RequestOrderType.getAllElements().forEach((item: CubiqTS.RequestOrderType) => {
          let itemTitle: string = this.translate.instant(
            "RequestOrderType." + CubiqTS.RequestOrderType[item],
          );
          let itemObject = new ICAutocompleteGenericModel(item, itemTitle);
          switch (item) {
            case CubiqTS.RequestOrderType.TP:
              this.orderType.dataset.push(itemObject);
              this.orderType.datasetWithoutTSL.push(itemObject);
              this.orderType.datasetWithoutMKT.push(itemObject);
              this.orderType.datasetWithoutTSL_MKT.push(itemObject);
              this.orderType.datasetGamma.push(itemObject);
              break;
            case CubiqTS.RequestOrderType.SL_ATRATE:
              if (config.slAtRateEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutTSL.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
                this.orderType.datasetWithoutTSL_MKT.push(itemObject);
                this.orderType.datasetGamma.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.SL_BID:
              if (config.slBidEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutTSL.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
                this.orderType.datasetWithoutTSL_MKT.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.SL_ASK:
              if (config.slAskEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutTSL.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
                this.orderType.datasetWithoutTSL_MKT.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.TSL:
              if (config.tSlAtRateEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.TSL_BID:
              if (config.tSlBidEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.TSL_ASK:
              if (config.tSlAskEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutMKT.push(itemObject);
              }
              break;
            case CubiqTS.RequestOrderType.BMARK:
              /* 	if(config.benchmarksEnabled){
									this.orderType.dataset.push(itemObject);
									this.orderType.datasetWithoutTSL.push(itemObject);
								} */
              break;
            case CubiqTS.RequestOrderType.ALGO:
              /* 	if(config.algosEnabled){
									this.orderType.dataset.push(itemObject);
									this.orderType.datasetWithoutTSL.push(itemObject);
								} */
              break;
            case CubiqTS.RequestOrderType.BMALGO:
              break;
            case CubiqTS.RequestOrderType.MARKET:
              if (config.marketOrdersEnabled) {
                this.orderType.dataset.push(itemObject);
                this.orderType.datasetWithoutTSL.push(itemObject);
              }
              break;
            default:
              this.orderType.dataset.push(itemObject);
              this.orderType.datasetWithoutTSL.push(itemObject);
              this.orderType.datasetWithoutMKT.push(itemObject);
              this.orderType.datasetWithoutTSL_MKT.push(itemObject);
              break;
          }
        });

        if (this.orderType.dataset != null && this.orderType.dataset.length > 1) {
          this.orderType.dataset.sort((a, b) => a["label"].localeCompare(b["label"]));
        }

        if (
          this.orderType.datasetWithoutTSL != null &&
          this.orderType.datasetWithoutTSL.length > 1
        ) {
          this.orderType.datasetWithoutTSL.sort((a, b) => a["label"].localeCompare(b["label"]));
        }

        if (
          this.orderType.datasetWithoutMKT != null &&
          this.orderType.datasetWithoutMKT.length > 1
        ) {
          this.orderType.datasetWithoutMKT.sort((a, b) => a["label"].localeCompare(b["label"]));
        }

        if (
          this.orderType.datasetWithoutTSL_MKT != null &&
          this.orderType.datasetWithoutTSL_MKT.length > 1
        ) {
          this.orderType.datasetWithoutTSL_MKT.sort((a, b) => a["label"].localeCompare(b["label"]));
        }

        if (this.orderType.datasetGamma != null && this.orderType.datasetGamma.length > 1) {
          this.orderType.datasetGamma.sort((a, b) => a["label"].localeCompare(b["label"]));
        }

        if (
          this.orderType.dataset != null &&
          this.orderType.dataset.length > 0 &&
          this.orderType.datasetWithoutTSL != null &&
          this.orderType.datasetWithoutTSL.length > 0 &&
          this.orderType.datasetWithoutMKT != null &&
          this.orderType.datasetWithoutMKT.length > 0 &&
          this.orderType.datasetWithoutTSL_MKT != null &&
          this.orderType.datasetWithoutTSL_MKT.length > 0 &&
          this.orderType.datasetGamma != null
        ) {
          this.control.orderTypeDatasetInitialized = true;
        } else {
          logger.out.error(`DEV-MKT`);
          this.control.orderTypeDatasetInitialized = false;
        }
      });
    });
  }

  /**
   * Retrieve all custom field definitions, strip out the non-enditable ones and keep sorted by ordinal ID
   */
  private initializeCustomFieldDefns(): void {
    this.customFieldService.getActiveCustomFields().then((customFields) => {
      const visibleFieldDefns: Array<CubiqTS.CustomField> = [];
      const nonvisibleFieldDefns: Array<CubiqTS.CustomField> = [];
      for (let defn of customFields) {
        if (defn.isEnabledFg) {
          // We only allow a maximum of 4 editable fields
          if (defn.showOnNewModifyOrderUiFg && visibleFieldDefns.length < 4) {
            visibleFieldDefns.push(defn);
          } else {
            nonvisibleFieldDefns.push(defn);
          }
        }
      }
      this.customFieldDefns.editable = visibleFieldDefns;
      this.customFieldDefns.noneditable = nonvisibleFieldDefns;
    });
  }

  /**
   * Load all currency pairs from the replicate provided base lookup service
   * and fill in the appropriate currency pair values (inverted too, where applicable)
   * in the data model
   */
  private counter = 0;
  public initializeCcyPairsDataset(): void {
    this.ccyPairs.datasetAll = [];
    this.ccyPairs.datasetAlgo = [];
    this.ccyPairs.datasetNdf = [];
    this.ccyPairs.datasetBmark = [];
    this.ccyPairs.datasetSpotFwd = [];
    this.ccyPairs.datasetSpotFwdMKT = [];
    this.ccyPairs.datasetGamma = [];
    this.ccyPairs.datasetNDFAlgo = [];

    this.ccyPairLookup.isPrimed().then(() => {
      // prime not synced.
      //let ccyPairLookupLocal: CcyPairLookupService = this.ccyPairLookup.getBase();
      //ccyPairLookupLocal.getAll().forEach((pair: CubiqTS.CurrencyPairImpl) => {
      this.ccyPairLookup.getAll().then((result: any) => {
        if (this.counter < 10 && (result == null || result.length < 1)) {
          this.counter++;
          setTimeout(() => {
            this.initializeCcyPairsDataset();
          }, 50);
        } else {
          result.forEach((pair: CubiqTS.CurrencyPairImpl) => {
            if (pair instanceof CubiqTS.CurrencyPairImpl) {
              /* SPOT-FWD */
              if (pair.spotEnabledFg || pair.fwdEnabledFg) {
                this.ccyPairs.datasetSpotFwd.push(pair.base + pair.nonBase);
                if (pair.allowInvertsFg) {
                  this.ccyPairs.datasetSpotFwd.push(pair.nonBase + pair.base);
                }
                /* SPOT-FWD MKT enabled */
                if (pair.allowMarketOrdersFg) {
                  this.ccyPairs.datasetSpotFwdMKT.push(pair.base + pair.nonBase);
                  if (pair.allowInvertsFg) {
                    this.ccyPairs.datasetSpotFwdMKT.push(pair.nonBase + pair.base);
                  }
                }
              }
              /* ALGO */
              if (pair.allowAlgoOrders) {
                this.ccyPairs.datasetAlgo.push(pair.base + pair.nonBase);
                if (pair.allowInvertAlgoOrders) {
                  this.ccyPairs.datasetAlgo.push(pair.nonBase + pair.base);
                }
              }
              /* NDF ALGO */
              if (pair.allowNdfAlgoOrders) {
                this.ccyPairs.datasetNDFAlgo.push(pair.base + pair.nonBase);
                if (pair.allowInvertAlgoOrders) {
                  this.ccyPairs.datasetNDFAlgo.push(pair.nonBase + pair.base);
                }
              }
              /* NDF */
              if (pair.allowNdfFwdTriggerFg || pair.allowNdfSpotTriggerFg) {
                this.ccyPairs.datasetNdf.push(pair.base + pair.nonBase);
                if (pair.allowInvertsFg) {
                  this.ccyPairs.datasetNdf.push(pair.nonBase + pair.base);
                }
              }
              /* BMARK */
              if (pair.allowBmarkSpotOrdersFg || pair.allowBmarkFwdOrdersFg) {
                this.ccyPairs.datasetBmark.push(pair.base + pair.nonBase);
                if (pair.allowInvertsFg) {
                  this.ccyPairs.datasetBmark.push(pair.nonBase + pair.base);
                }
              }
              /* GAMMA */
              if (pair.allowGammaOrdersFg && pair.spotEnabledFg) {
                this.ccyPairs.datasetGamma.push(pair.base + pair.nonBase);
                if (pair.allowInvertsFg) {
                  this.ccyPairs.datasetGamma.push(pair.nonBase + pair.base);
                }
              }
              /* ALL */
              this.ccyPairs.datasetAll.push(pair.base + pair.nonBase);
              if (pair.allowInvertsFg) {
                this.ccyPairs.datasetAll.push(pair.nonBase + pair.base);
              }
            }
          });
          /* sorting */
          this.ccyPairs.datasetAll.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetNdf.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetBmark.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetAlgo.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetNDFAlgo.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetSpotFwd.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetSpotFwdMKT.sort((a, b) => a.localeCompare(b));
          this.ccyPairs.datasetGamma.sort((a, b) => a.localeCompare(b));
          // error reporting
          if (this.ccyPairs.datasetAll == null || this.ccyPairs.datasetAll.length < 1) {
            logger.out.error(
              `ERROR: oeDataset.initializeCcyPairsDataset(); ccyPair dataset is empty!`,
            );
          }
        }
      });
    });
  }

  private fixingCodeCounter = 0;
  public initializeFixingCodeDataset(): void {
    this.fixingSources.dataset = [];
    this.fixingSourceLookup.isPrimed().then(() => {
      this.fixingSourceLookup.getAllAdaptedFixingSources().then((result: any) => {
        if (this.fixingCodeCounter < 10 && (result == null || result.length < 1)) {
          this.fixingCodeCounter++;
          setTimeout(() => {
            this.initializeFixingCodeDataset();
          }, 50);
        } else {
          result.forEach((fixingSource: any) => {
            let itemObject = new ICFixingSourceModel(
              fixingSource.label,
              fixingSource.label,
              fixingSource.id,
              fixingSource.pair,
              fixingSource.isDefault,
              fixingSource.isCustom,
            );

            this.fixingSources.dataset.push(itemObject);
            if (this.fixingSources.dataset.length >= result.length)
              this.fixingSources.dataset.sort((a: ICFixingSourceModel, b: ICFixingSourceModel) =>
                a.label.localeCompare(b.label),
              );
          });
        }
      });
    });
  }

  public initializeBmFixingCodeDataset(): void {
    this.bmFixingSources.dataset = [];
    this.benchmarkFixingService.isPrimed().then(() => {
      this.benchmarkFixingService.getAll().then((fixings: CubiqTS.BenchmarkFixingImpl[]) => {
        this.bmFixingSources.allBmFixings = fixings;
        this.bmFixingSources.dataset = fixings.map(
          (f) => new ICFixingSourceModel(f.benchmarkCd, f.benchmarkDesc),
        );
      });
    });
  }

  protected onFixingSourceUpdated(modelEvent: Array<any>): void {
    this.initializeFixingCodeDataset();
  }

  protected onBmFixingSourceUpdated(modelEvent: Array<any>): void {
    this.initializeBmFixingCodeDataset();
  }

  /**
   * Get the set of tenors for the specified fixing date. If date or time is null  then Spot tenors for now
   * will be returned
   * @param base
   * @param nonBase
   * @param fixingDate
   * @param fixingTime
   */
  public getFixingCalendar(
    base: string,
    nonBase: string,
    fixingDate: Date,
    fixingTime: BenchmarkFixingTime,
    fwdEnabled: boolean,
  ): Promise<MappedCurrencyPairCalendarImpl> {
    let standardDate: CubiqTS.StandardDateImpl = null;
    let standardTime: CubiqTS.StandardTimeImpl = null;

    if (
      fixingDate != null &&
      fixingDate instanceof Date &&
      fixingTime != null &&
      typeof fixingTime === "object" &&
      typeof fixingTime.value === "string"
    ) {
      // Create standard date/time values
      standardDate = new CubiqTS.StandardDateImpl();
      standardDate.day = fixingDate.getDate();
      standardDate.month = fixingDate.getMonth() + 1;
      standardDate.year = fixingDate.getFullYear();

      standardTime = new CubiqTS.StandardTimeImpl();
      standardTime.hours = Number(fixingTime.value.substring(0, 2));
      standardTime.minutes = Number(fixingTime.value.substring(3, 5));
      standardTime.seconds = 0;
      standardTime.timeZone = fixingTime.timezoneCd;
    }
    return this.benchmarkCalendarService
      .getFixingCalendar(base, nonBase, standardDate, standardTime)
      .then((calendar: MappedCurrencyPairCalendarImpl) => {
        if (
          Array.isArray(calendar.tenorToValueDateSet) &&
          calendar.tenorToValueDateSet.length > 1 &&
          !fwdEnabled
        ) {
          // Spot-only so create new calendar with just spot
          const reducedCalendar = Object.assign({}, calendar);
          // let reducedCalendar = JSON.parse(JSON.stringify(calendar));
          reducedCalendar.tenorToValueDateMap = new Map();
          reducedCalendar.tenorToValueDateSet = [];

          let spotItem = calendar.tenorToValueDateMap.get(CubiqTS.Tenor.SPOT);
          if (spotItem != null) {
            reducedCalendar.tenorToValueDateMap.set(CubiqTS.Tenor.SPOT, spotItem);
            reducedCalendar.tenorToValueDateSet.push(spotItem);
          } else {
            logger.out.error(`spotTenorToValueDateMapItem not found`);
          }
          return reducedCalendar;
        } else {
          return calendar;
        }
      });
  }
}
